import React, { useEffect, useMemo, useRef, useState } from 'react';
import Section from './Section';
import { getMargin } from './Table';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const SortAscIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 19V5M12 5l-6 6M12 5l6 6" />
  </svg>
);

const SortDescIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 5v14M12 19l-6-6M12 19l6-6" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ThemeIcon = ({ theme }) => theme === 'light' ? (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
    <circle cx="12" cy="12" r="4" />
  </svg>
) : (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 15.2A8.5 8.5 0 0 1 8.8 4a8.5 8.5 0 1 0 11.2 11.2Z" />
  </svg>
);

const getLatestUpdatedAt = (data) => {
  return data.reduce((latest, company) => {
    const value = company.updated_at || company.updatedAt;
    const timestamp = Date.parse(value);

    if (!Number.isFinite(timestamp)) return latest;
    return !latest || timestamp > latest.timestamp
      ? { value, timestamp }
      : latest;
  }, null)?.value;
};

const MainContainer = ({
  theme,
  onToggleTheme,
  view,
  onChangeView,
  ncavData,
  epsxData,
  cspData,
  loading,
  error,
  isAuthenticated,
  onShowLogin,
  onLogout,
}) => {
  const [activeMethod, setActiveMethod] = useState('ncav');
  const [query, setQuery] = useState('');
  const [sortOrder, setSortOrder] = useState(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortControlRef = useRef(null);
  const nextTheme = theme === 'light' ? 'dark' : 'light';
  const isCspView = view === 'csp';

  useEffect(() => {
    if (!isSortOpen) return undefined;

    const handleClickOutside = (event) => {
      if (sortControlRef.current && !sortControlRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen]);

  useEffect(() => {
    setQuery('');
    setSortOrder(null);
  }, [view]);

  const handleSortSelect = (order) => {
    setSortOrder((current) => (current === order ? null : order));
    setIsSortOpen(false);
  };

  const activeMethodKey = isCspView ? 'csp' : activeMethod;
  const activeData = isCspView ? cspData : (activeMethod === 'ncav' ? ncavData : epsxData);
  const updatedAt = getLatestUpdatedAt(activeData);
  const filteredData = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const matches = !normalizedQuery
      ? activeData
      : activeData.filter((company) => {
        const searchableFields = [
          company.ticker,
          company.symbol,
          company.name,
          company.company_name,
          company.companyName,
        ];

        return searchableFields.some((field) =>
          String(field || '').toLowerCase().includes(normalizedQuery)
        );
      });

    if (!sortOrder) return matches;

    return [...matches].sort((a, b) => {
      const marginA = Number(getMargin(a, activeMethodKey));
      const marginB = Number(getMargin(b, activeMethodKey));
      const hasA = Number.isFinite(marginA);
      const hasB = Number.isFinite(marginB);

      if (!hasA && !hasB) return 0;
      if (!hasA) return 1;
      if (!hasB) return -1;

      return sortOrder === 'asc' ? marginA - marginB : marginB - marginA;
    });
  }, [activeData, query, sortOrder, activeMethodKey]);

  const totalCompanies = new Set(
    (isCspView ? cspData : [...ncavData, ...epsxData])
      .map((company) => company.ticker || company.symbol)
      .filter(Boolean)
  ).size;

  return (
    <main className="page-shell">
      <div className="page-content">
        <nav className="view-switch" aria-label="Screener view">
          <button
            type="button"
            className={!isCspView ? 'active' : ''}
            onClick={() => onChangeView('valuation')}
            aria-pressed={!isCspView}
          >
            Valuation methods
          </button>
          <button
            type="button"
            className={isCspView ? 'active' : ''}
            onClick={() => onChangeView('csp')}
            aria-pressed={isCspView}
          >
            Cash-secured puts candidates
          </button>
        </nav>

        <header className="hero-panel">
          <div className="hero-copy">
            {isCspView ? (
              <>
                <span className="eyebrow">Cash-secured put screener</span>
                <h1>Puts, sold with a cushion.</h1>
                <p>
                  Explore companies trading well below their estimated fair value —
                  candidates worth considering for selling cash-secured puts.
                </p>
              </>
            ) : (
              <>
                <span className="eyebrow">Fundamental value screener</span>
                <h1>Value, made visible.</h1>
                <p>
                  Explore companies trading below their estimated value through two
                  focused valuation lenses.
                </p>
              </>
            )}
          </div>

          <div className="hero-actions">
            <div className="hero-action-buttons">
              <button
                type="button"
                className={`theme-toggle target-${nextTheme}`}
                onClick={onToggleTheme}
                aria-pressed={theme === 'dark'}
                aria-label={`Switch to ${nextTheme} mode`}
                title={`Switch to ${nextTheme} mode`}
              >
                <ThemeIcon theme={nextTheme} />
                <span>Switch to {nextTheme === 'light' ? 'Light' : 'Dark'}</span>
              </button>

              <button
                type="button"
                className="auth-button"
                onClick={isAuthenticated ? onLogout : onShowLogin}
              >
                <span>{isAuthenticated ? 'Log out' : 'Log in'}</span>
              </button>
            </div>

            <div className="market-summary" aria-label="Valuation coverage">
              <div className="summary-item">
                <span>Companies</span>
                <strong>{loading ? '—' : totalCompanies}</strong>
              </div>
              {isCspView ? (
                <div className="summary-item">
                  <span>Min. margin</span>
                  <strong>100%+</strong>
                </div>
              ) : (
                <div className="summary-item">
                  <span>Models</span>
                  <strong>2</strong>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="filter-panel" aria-label="Opportunity filters">
          <div className="filter-row">
            <label className="search-field">
              <SearchIcon />
              <span className="sr-only">Search companies</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by symbol or company name"
              />
            </label>

            <div className="sort-control" ref={sortControlRef}>
              <button
                type="button"
                className={`sort-trigger ${sortOrder ? 'active' : ''}`}
                onClick={() => setIsSortOpen((open) => !open)}
                aria-haspopup="true"
                aria-expanded={isSortOpen}
              >
                <span>Sort by</span>
                <ChevronDownIcon />
              </button>

              {isSortOpen && (
                <div className="sort-menu" role="menu" aria-label="Sort by margin of safety">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={sortOrder === 'asc'}
                    className={sortOrder === 'asc' ? 'active' : ''}
                    onClick={() => handleSortSelect('asc')}
                  >
                    <span>Margin of safety</span>
                    <SortAscIcon />
                  </button>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={sortOrder === 'desc'}
                    className={sortOrder === 'desc' ? 'active' : ''}
                    onClick={() => handleSortSelect('desc')}
                  >
                    <span>Margin of safety</span>
                    <SortDescIcon />
                  </button>
                </div>
              )}
            </div>
          </div>

          {!isCspView && (
            <nav className="method-tabs" aria-label="Valuation method">
              <button
                type="button"
                className={activeMethod === 'ncav' ? 'active' : ''}
                onClick={() => setActiveMethod('ncav')}
                aria-pressed={activeMethod === 'ncav'}
              >
                <span>NCAV</span>
                <small>Net current asset value</small>
                <b>{ncavData.length}</b>
              </button>
              <button
                type="button"
                className={activeMethod === 'epsx' ? 'active' : ''}
                onClick={() => setActiveMethod('epsx')}
                aria-pressed={activeMethod === 'epsx'}
              >
                <span>EPS-X</span>
                <small>Earnings multiple value</small>
                <b>{epsxData.length}</b>
              </button>
            </nav>
          )}
        </section>

        <Section
          method={activeMethodKey}
          title={isCspView ? 'Cash-secured put candidates' : 'Opportunities'}
          description={isCspView
            ? 'Companies trading well below fair value — candidates for selling cash-secured puts.'
            : 'Companies trading below their estimated fundamental value.'}
          data={filteredData}
          updatedAt={updatedAt}
          loading={loading}
          error={error}
          hasQuery={Boolean(query.trim())}
        />
      </div>
    </main>
  );
};

export default MainContainer;
